import { commerceOfferProperties } from "@/lib/analytics-ga4";
import { trackEvent, trackOwnedAnalyticsOnly } from "@/lib/analytics-events";
import type { CheckoutChannel } from "@/lib/checkout-log-domain";
import type { CommerceSystem } from "@/lib/commerce-routing";

// ── AI Astrologer / Chat funnel events ──
// Added 2026-04-25 to enable funnel diagnosis. Previously the AI Astrologer
// flow had zero instrumentation — we knew kundali_generated and
// report_purchased fired, but nothing in between. These eight events let
// us see conversion at each stage: page view → persona click → chat start
// → message N → paywall shown → paid CTA clicked → purchased.

export function trackAstrologerPageViewed(props: {
  language?: string;
  source?: string; // referrer slug
  source_path?: string;
}) {
  trackEvent("astrologer_page_viewed", props);
}

export function trackAIAstrologerOpened(props: {
  language?: string;
  source?: string;
  source_path?: string;
  placement?: string;
}) {
  trackEvent("ai_astrologer_opened", props);
}

export function trackChatStarted(props: {
  reading_id: string;
  language: string;
  persona_id?: string;
}) {
  trackEvent("chat_started", props);
}

export function trackChatMessageSent(props: {
  reading_id: string;
  message_index: number; // 1-based position of THIS user message
  message_chars: number;
  language: string;
  source_path?: string;
  intent?: string;
  landing_intent?: string;
  detected_intent?: string;
  intent_confidence?: string;
  paywall_variant?: string;
}) {
  trackEvent("chat_message_sent", props);
}

/**
 * Fired when the LLM invokes an engine tool mid-turn (step 6 agentic
 * chat). Pairs with the backend's structured-log `chat.tool` event so
 * BigQuery + GA4 can both compute tool invocation rate, p95 latency, and
 * conversion lift from agentic vs. text-only turns.
 *
 * Outcome values:
 *   - "pending"  : tool_call chunk arrived (LLM asked to call)
 *   - "done"     : tool_result chunk arrived (engine returned cleanly)
 *   - "error"    : output-error state on the SDK chunk
 */
export function trackChatToolInvoked(props: {
  reading_id: string;
  tool_name: string;
  outcome: "pending" | "done" | "error";
  message_index: number;
  language: string;
}) {
  trackEvent("chat_tool_invoked", props);
}

export function trackFirstMeaningfulQuestion(props: {
  reading_id: string;
  message_index: number;
  message_chars: number;
  language: string;
  source_path?: string;
  intent?: string;
  landing_intent?: string;
  detected_intent?: string;
  intent_confidence?: string;
  paywall_variant?: string;
}) {
  trackEvent("first_meaningful_question", props);
  // Funnel-canonical alias — see business/ishvaram/data/funnels/screen_surfaces.generated.yaml
  trackOwnedAnalyticsOnly("first_msg", props);
}

export function trackAgentAnswerGiven(props: {
  reading_id: string;
  message_index: number;
  answer_chars: number;
  language: string;
  session_id?: string;
  source_path?: string;
  intent?: string;
  paywalled?: boolean;
  landing_intent?: string;
  detected_intent?: string;
  intent_confidence?: string;
  paywall_variant?: string;
  answer_origin?: "server" | "local";
  message_id_available?: boolean;
  message_id?: string;
}) {
  trackEvent("agent_answer_given", props);
  trackEvent("ai_chat_reply_received", props);
}

export function trackChatMessageRatingControlShown(props: {
  reading_id: string;
  message_id: string;
  fresh_answer: true;
}) {
  trackEvent("chat_message_rating_control_shown", props);
}

export function trackChatMessageRatingSubmitted(props: {
  reading_id: string;
  message_id: string;
  rating: "up" | "down";
  submission_stage: "direction" | "classified";
  reason?: string;
}) {
  trackEvent("chat_message_rating_submitted", props);
}

export function trackChatMessageRatingFailed(props: {
  reading_id: string;
  message_id: string;
  rating: "up" | "down";
  submission_stage: "direction" | "classified";
  reason?: string;
}) {
  trackEvent("chat_message_rating_failed", props);
}

export function trackRazorpayCheckoutOpened(props: {
  reading_id?: string;
  order_id: string;
  plan_id: string;
  plan_type: string;
  price_inr?: number;
  value?: number;
  currency?: string;
  amount_minor?: number;
  source?: string;
  placement?: string;
  checkout_channel?: CheckoutChannel;
  commerce_system?: CommerceSystem;
  intent?: string;
  source_path?: string;
  customer_problem?: string;
  journey_action_id?: string;
  concern_id?: string;
  agent_trace_id?: string;
  landing_intent?: string;
  detected_intent?: string;
  intent_confidence?: string;
  is_primary_cta?: boolean;
  cta_rank?: number;
  paywall_variant?: string;
}) {
  trackEvent("razorpay_checkout_opened", props);
  // Also fire payment_started — the funnel-stage canonical event referenced
  // by screens/checkout-started.md as events.primary. Same intent (Razorpay
  // popup opened = user initiated payment); dual-fire keeps the verbose
  // 'razorpay_checkout_opened' name for backend correlation while feeding
  // the funnel layer through its canonical stage name.
  trackEvent("payment_started", props);
}

export function trackRazorpayCheckoutClosed(props: {
  reading_id?: string;
  order_id: string;
  plan_id: string;
  plan_type: string;
  price_inr?: number;
  value?: number;
  currency?: string;
  amount_minor?: number;
  close_reason: "dismissed" | "payment_success" | "payment_failed" | "open_failed";
  source?: string;
  placement?: string;
  checkout_channel?: string;
  commerce_system?: string;
  intent?: string;
  source_path?: string;
  customer_problem?: string;
  journey_action_id?: string;
  concern_id?: string;
  agent_trace_id?: string;
  landing_intent?: string;
  detected_intent?: string;
  intent_confidence?: string;
  is_primary_cta?: boolean;
  cta_rank?: number;
  paywall_variant?: string;
}) {
  trackEvent("razorpay_checkout_closed", props);
}

export function trackCommerceCheckoutStarted(props: {
  reading_id?: string;
  plan_id: string;
  offer_type: string;
  price_inr: number;
  value?: number;
  currency?: string;
  amount_minor?: number;
  source?: string;
  placement?: string;
  checkout_channel?: CheckoutChannel;
  commerce_system?: CommerceSystem;
  intent?: string;
  source_path?: string;
  customer_problem?: string;
  journey_action_id?: string;
  concern_id?: string;
  agent_trace_id?: string;
  landing_intent?: string;
  detected_intent?: string;
  intent_confidence?: string;
  is_primary_cta?: boolean;
  cta_rank?: number;
  paywall_variant?: string;
}) {
  trackEvent("commerce_checkout_started", commerceOfferProperties(props));
}

export function trackPremiumUnlockShown(props: {
  reading_id: string;
  message_index: number;
  plan_id?: string;
  price_inr?: number;
  value?: number;
  currency?: string;
  amount_minor?: number;
  placement: string;
  offer_type?: string;
  checkout_channel?: string;
  commerce_system?: string;
  intent?: string;
  source_path?: string;
  page_category?: string;
  customer_problem?: string;
  landing_intent?: string;
  detected_intent?: string;
  intent_confidence?: string;
  is_primary_cta?: boolean;
  cta_rank?: number;
  paywall_variant?: string;
}) {
  trackEvent("premium_unlock_shown", props);
}

export function trackReportDelivered(props: {
  reading_id?: string;
  order_id: string;
  plan_id: string;
  delivery_channel: "download" | "email" | "whatsapp" | "unknown";
  report_job_id?: string;
  report_job_status?: string;
  source_path?: string;
  intent?: string;
  checkout_channel?: string;
  commerce_system?: string;
}) {
  trackEvent("report_delivered", props);
}

export function trackPaywallShown(props: {
  reading_id: string;
  message_index: number;
  reason: string; // "time_window_exhausted" | "credit_exhausted" etc.
  placement?: string;
  intent?: string;
  source_path?: string;
  customer_problem?: string;
  landing_intent?: string;
  detected_intent?: string;
  intent_confidence?: string;
  paywall_variant?: string;
}) {
  trackEvent("paywall_shown", props);
  trackEvent("chat_limit_reached", props);
  // Funnel-canonical alias — see business/ishvaram/data/funnels/screen_surfaces.generated.yaml
  trackOwnedAnalyticsOnly("paywall_viewed", props);
}

export function trackPaidCtaClicked(props: {
  reading_id: string;
  plan_id: string;
  plan_type: string;
  price_inr: number;
  value?: number;
  currency?: string;
  amount_minor?: number;
  source: string; // "paywall" | "kundali_result"
  placement?: string;
  checkout_channel?: string;
  commerce_system?: string;
  intent?: string;
  source_path?: string;
  customer_problem?: string;
  journey_action_id?: string;
  concern_id?: string;
  landing_intent?: string;
  detected_intent?: string;
  intent_confidence?: string;
  is_primary_cta?: boolean;
  cta_rank?: number;
  paywall_variant?: string;
}) {
  trackEvent("paid_cta_clicked", props);
  trackEvent(
    "commerce_offer_clicked",
    commerceOfferProperties({
      ...props,
      offer_type: props.plan_type,
      checkout_channel: props.checkout_channel,
      commerce_system: props.commerce_system,
    })
  );
}

export {
  trackSEOAgentBridgeClicked,
  trackSEOAgentBridgeViewed,
  trackSEOIntentCaptured,
} from "@/lib/analytics-seo-bridge";
export * from "@/lib/analytics-agent-offers";
